import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';

const downloadBlob = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const dateStamp = () => new Date().toISOString().split('T')[0];

export const useOdmConfig = () => {
  const [odmConfig, setOdmConfig] = useState(null);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClaims, setSelectedClaims] = useState([]);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [configRes, claimsRes] = await Promise.all([
        api.get('/odm/config'),
        api.get('/claims', { params: { status: 'Pending' } }),
      ]);
      setOdmConfig(configRes.data);
      setClaims(claimsRes.data);
    } catch (error) {
      toast.error('Failed to load ODM configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportClaims = async (path, extension, label) => {
    setExporting(true);
    try {
      const response = await api.get(path, {
        params: { status: 'Pending' },
        responseType: 'blob',
      });
      downloadBlob(response.data, `${extension === 'edi' ? '837P' : 'pnm_export'}_${dateStamp()}.${extension}`);
      toast.success(`${label} exported successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExport837P = () =>
    exportClaims('/odm/claims/export-837p', 'edi', 'EDI 837P file');
  const handleExportPNM = () =>
    exportClaims('/odm/claims/pnm-export', 'csv', 'PNM format file');

  const handleMarkSubmitted = async () => {
    if (selectedClaims.length === 0) {
      toast.error('Select claims to mark as submitted');
      return;
    }
     
    const tcn = prompt('Enter ODM Transaction Control Number (TCN):');
    if (!tcn) return;
    try {
      await api.post('/odm/claims/mark-submitted', selectedClaims, { params: { tcn } });
      toast.success('Claims marked as submitted');
      setSelectedClaims([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to update claims');
    }
  };

  const toggleAllClaims = (checked) => {
    setSelectedClaims(checked ? claims.map((c) => c.id) : []);
  };

  const toggleClaim = (claimId, checked) => {
    setSelectedClaims((prev) =>
      checked ? [...prev, claimId] : prev.filter((id) => id !== claimId)
    );
  };

  return {
    odmConfig,
    claims,
    loading,
    selectedClaims,
    exporting,
    handleExport837P,
    handleExportPNM,
    handleMarkSubmitted,
    toggleAllClaims,
    toggleClaim,
  };
};
